import React from "react";
import "./DepartmentStyles.css";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";

const AddDepartmentComponent = (props) => {
  console.log("AddDepartmentComponent props:", props);

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    props.handleUseDefaultChange(e);
  };

  // Handle department name change
  const handleDepartmentNameChange = (index, value) => {
    props.handleDepartmentChange(index, 'name', value);
  };

  // Handle initial balance change
  const handleInitialBalanceChange = (index, value) => {
    props.handleBalanceChange(index, value);
  };

  // Handle default balance change
  const handleDefaultBalanceChange = (e) => {
    if (props.handleDefaultBalanceChange) {
      props.handleDefaultBalanceChange(e.target.value);
    }
  };

  // Add new department
  const handleAddDepartment = () => {
    if (props.handleAddDepartment) {
      props.handleAddDepartment();
    }
  };

  // Remove department
  const handleRemoveDepartment = (index) => {
    if (props.handleRemoveDepartment) {
      props.handleRemoveDepartment(index);
    }
  };

  // Filter out already selected departments
  const getAvailableDepartmentOptions = (currentIndex) => {
    if (!props.availableDepartments || !Array.isArray(props.availableDepartments)) {
      return [];
    }

    return props.availableDepartments.filter(dept => {
      // Check if this department is already selected in other rows (except current one)
      const isSelectedElsewhere = props.departments.some((deptItem, idx) => 
        idx !== currentIndex && deptItem.departmentId === dept.id
      );
      return !isSelectedElsewhere;
    });
  };

  return (
    <div className="department-setup-container">
      <div className="department-setup-card">
        <Box className="department-setup-box">
          {/* Header */}
          <div className="department-setup-header">
            <h2 className="department-setup-title">Departments</h2>
            <p className="department-setup-subtitle">
              Setup departments & balances
            </p>
          </div>

          {/* Checkbox Section */}
          <div className="department-checkbox-section">
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.useDefaultDepartment || false}
                  onChange={handleCheckboxChange}
                  className="department-checkbox"
                  disabled={props.loading || props.fetchingDepartments}
                />
              }
              label="Use Default Department Only"
            />
            <p className="department-checkbox-hint">
              {props.useDefaultDepartment 
                ? "Default balance will be applied to all users" 
                : "You can add and configure multiple departments below"}
            </p>
          </div>

          {/* Loading indicator for departments */}
          {props.fetchingDepartments && (
            <div className="departments-loading">
              <CircularProgress size={20} style={{ marginRight: "10px" }} />
              <span>Loading departments...</span>
            </div>
          )}

          {/* Main Content Area */}
          <div className="department-main-content">
            {/* When checkbox is checked - Show Default Balance Field */}
            {props.useDefaultDepartment ? (
              <div className="default-department-section">
                <div className="department-section">
                  <Typography variant="h6" className="department-section-title">
                    DEFAULT SETTINGS
                  </Typography>
                  <p className="default-department-info">
                    When checked, all users will use default department settings
                  </p>
                  
                  <div className="department-fields">
                    {/* Default Balance Field */}
                    <div className="department-field-group">
                      <label className="department-field-label">
                        Default Balance (₹) *
                      </label>
                      <TextField
                        className="department-balance-input"
                        fullWidth
                        placeholder="1000"
                        value={props.defaultBalance || ""}
                        onChange={handleDefaultBalanceChange}
                        type="text"
                        disabled={props.loading}
                        InputProps={{
                          startAdornment: (
                            <span className="currency-symbol">₹</span>
                          ),
                        }}
                        inputProps={{
                          step: "0.01",
                          min: "0"
                        }}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* When checkbox is unchecked - Show Departments List */
              <>
                {/* Departments List Container - This area scrolls */}
                <div className="departments-list-container">
                  {props.departments && props.departments.map((dept, index) => (
                    <div key={index} className="department-section">
                      <div className="department-header">
                        <Typography variant="h6" className="department-section-title">
                          DEPARTMENT {index + 1}
                        </Typography>
                        {index !== 0 && (
                          <IconButton
                            onClick={() => handleRemoveDepartment(index)}
                            className="remove-department-button"
                            size="small"
                            disabled={props.loading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </div>
                      
                      <div className="department-fields">
                        {/* Department Name */}
                        <div className="department-field-group">
                          <label className="department-field-label">
                            Department Name *
                          </label>
                          <FormControl fullWidth className="department-select">
                            <InputLabel>Select department</InputLabel>
                            <Select
                              value={dept.name || ""}
                              onChange={(e) => handleDepartmentNameChange(index, e.target.value)}
                              label="Select department"
                              className="department-select-input"
                              disabled={props.loading || props.fetchingDepartments}
                            >
                              <MenuItem value="">
                                <em>Select department</em>
                              </MenuItem>
                              {getAvailableDepartmentOptions(index).map(option => (
                                <MenuItem key={option.id} value={option.name}>
                                  {option.name} (ID: {option.id})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>

                        {/* Initial Balance */}
                        <div className="department-field-group">
                          <label className="department-field-label">
                            Initial Balance (₹) *
                          </label>
                          <TextField
                            className="department-balance-input"
                            fullWidth
                            placeholder="1000"
                            value={dept.initialBalance || ""}
                            onChange={(e) => handleInitialBalanceChange(index, e.target.value)}
                            type="text"
                            disabled={props.loading}
                            InputProps={{
                              startAdornment: (
                                <span className="currency-symbol">₹</span>
                              ),
                            }}
                            inputProps={{
                              step: "0.01",
                              min: "0"
                            }}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Department Button - Fixed at bottom */}
                <div className="add-department-section">
                  <Button
                    className="add-department-button"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddDepartment}
                    disabled={props.loading || props.fetchingDepartments}
                  >
                    Add Department
                  </Button>
                  {/* <p className="add-department-hint">
                    {props.departments ? props.departments.length : 0} department(s) added
                  </p> */}
                </div>
              </>
            )}
          </div>

          {/* Loading Indicator */}
          {props.loading && (
            <div className="department-loading-indicator">
              <CircularProgress size={24} />
              <span>Processing...</span>
            </div>
          )}
        </Box>
      </div>
    </div>
  );
};

export default AddDepartmentComponent;